import { useCallback } from 'react';
import { cn } from '@/lib/core/utils';

/**
 * A 1×1 fully transparent GIF, used as the poster of last resort.
 *
 * Chrome on Android paints its own default poster — a grey plate with a play glyph — on any
 * `<video>` that has no `poster` and has not yet decoded a first frame. Safari/WKWebView and
 * desktop Chrome paint nothing, which is why the artefact only ever showed up on Android. Any
 * poster at all suppresses that default, so a transparent pixel is enough: it lets the parent's
 * own background show through for the fraction of a second before the first frame lands, instead
 * of flashing a colour that would have to be kept in sync with three different screens.
 */
const BLANK_POSTER = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

export interface BackgroundVideoProps {
  /** Absolute media URL. Nothing renders when it is missing, so callers can pass CMS data straight in. */
  src?: string;
  /** Still frame to hold until the video decodes. Falls back to a transparent pixel. */
  poster?: string;
  /** Merged over the full-bleed base classes. */
  className?: string;
}

/**
 * Decorative full-bleed video backdrop, as used behind the Explore, Shop and Splash screens.
 *
 * The three screens had drifted into three near-identical `<video>` tags, each missing the same
 * Android-specific attributes. Keeping the markup in one place is the point of the component: the
 * attributes below are not cosmetic defaults but the conditions for the element to autoplay
 * silently and without chrome inside a WebView.
 *
 * `preload="auto"` is set because the default (`metadata`) makes Chrome Android fetch the headers
 * and stop, decoding no frame to paint, which stretches out the poster window it is trying to fill.
 *
 * `muted` is written twice — once as the React prop, once as a DOM attribute in the ref — because
 * React only ever sets the property, and an autoplay policy that inspects the attribute would see
 * an unmuted video and refuse. Setting it in the ref also guarantees it lands before `play()`.
 */
export function BackgroundVideo({ src, poster, className }: BackgroundVideoProps) {
  // Keyed on `src` so that swapping the source re-runs the kick-off; a ref callback with a stable
  // identity would only ever fire on mount, leaving a replaced video paused on its poster.
  const startPlayback = useCallback(
    (element: HTMLVideoElement | null) => {
      if (!element) return;
      element.muted = true;
      element.setAttribute('muted', '');
      // A rejected play() is the browser's autoplay policy talking, not an error to surface: the
      // element is decorative and the poster is already covering the gap.
      void element.play?.().catch(() => {});
    },
    [src]
  );

  if (!src) return null;

  return (
    <video
      ref={startPlayback}
      className={cn('pointer-events-none absolute inset-0 h-full w-full object-cover', className)}
      src={src}
      poster={poster ?? BLANK_POSTER}
      preload="auto"
      autoPlay
      muted
      loop
      playsInline
      aria-hidden="true"
    />
  );
}
